import React from 'react';
import clsx from 'clsx';
import { useSelector } from 'react-redux';
import { useRouteParams } from '@/context/routeParamsContext';
import { getOneCmsLoungeFinderImagesBaseUrl } from '@/selectors/oneCmsSelectors';
import { cardArtMapping } from '@/constants/productMapping';
import { getCountryCode, getCardArtUrl } from '@/helpers/utils';
import { getLocaleData } from '@/selectors/localeSelectors';
import { useModal } from '@/context/modalContext';
import { useCommonContent } from '@/hooks/oneCmsIntlHooks/oneCmsIntlHelperHooks';
import { useAnalyticsContext } from '@/context/analyticsContext';
import styles from './GlobalHeader.module.scss';
import LoungeFinderBreadcrumb from './LoungeFinderBreadcrumb';
import AirportsSearchBox from './AirportsSearchBox';
import LFLink from './LFLink';
import { modalTypes } from './modals/constants';

const GlobalHeader = () => {
  const { product, loungeId, airportCode } = useRouteParams();
  const baseOneCmsImgUrl = useSelector(getOneCmsLoungeFinderImagesBaseUrl);
  const { locale } = useSelector(getLocaleData);
  const imgSrc = getCardArtUrl({
    baseOneCmsImgUrl,
    product: cardArtMapping[product],
    locale,
  });
  const { customTrack } = useAnalyticsContext();
  const { setModalName } = useModal();

  const handleToggleModal = (e) => {
    setModalName(modalTypes.SELECT_CARD_MODAL);
    customTrack({ event: e, detail: 'global header' });
  };
  const { header, productDisplayNames } = useCommonContent();
  const productName = `${productDisplayNames[product]?.displayName} (${getCountryCode(product, locale)})`;

  return (
    <div className="dls-deep-blue-bg tw-relative tw-z-10">
      <div className="tw-mx-auto tw-flex tw-flex-col tw-p-3 lg_tw-max-w-320 lg_tw-px-10">
        <div
          className={clsx('tw-grid tw-grid-cols-3 tw-items-center tw-gap-4')}
        >
          <div className="lg_tw-py-3">
            <LFLink
              detail="logo-link"
              element="logo-link"
              to={`${product || 'the-platinum-card'}`}
              className="tw-inline-flex hover_tw-underline hover_tw-decoration-gray-100"
            >
              <div className="tw-flex tw-items-center tw-font-sans tw-text-base tw-leading-6 tw-text-gray-100">
                <p className="tw-font-bold">{header?.title}</p>
                <span className="tw-mx-2 tw-h-5 tw-w-px tw-bg-gray-100" />
                <p className="tw-font-normal">{header?.label}</p>
              </div>
            </LFLink>
          </div>
          <div>
            {airportCode && (
              <div className="tw-hidden tw-z-1-important lg_tw-block">
                <AirportsSearchBox className={styles.searchBar} />
              </div>
            )}
          </div>
          <div className="tw-flex tw-justify-end">
            <button
              className="tw-fit"
              type="button"
              onClick={handleToggleModal}
            >
              <div
                id="global-header-card-switcher-button"
                className={clsx(
                  'tw-flex hover_tw-bg-blue-deep-sb8-brightBlue-16',
                  styles.miniCallout
                )}
              >
                <img
                  src={imgSrc}
                  alt="Card Callout Notification"
                  className="tw-flex tw-h-6 tw-w-9dot5 tw-items-center tw-justify-center"
                />
                <span>{productName}</span>
              </div>
            </button>
          </div>
        </div>
        {airportCode && (
          <div className="tw-mt-3 tw-block tw-z-1-important lg_tw-hidden">
            <AirportsSearchBox className={styles.searchBar} />
          </div>
        )}
      </div>
      {loungeId && (
        <div className="tw-mx-3 tw-flex tw-max-w-300 tw-items-center tw-border-t tw-border-blue-deep-tint-700-t12 tw-py-2dot5 xl_tw-mx-auto">
          <LoungeFinderBreadcrumb loungeId={loungeId} />
        </div>
      )}
    </div>
  );
};

export default GlobalHeader;
